import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Button, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTour } from "../context/TourContext";

const STEPS = [
  { target: "tour-morning-btn",       page: "/gauges",   tab: null,         titleKey: "tour.steps.morningBtn.title",       textKey: "tour.steps.morningBtn.text" },
  { target: "tour-situation-btn",     page: "/gauges",   tab: null,         titleKey: "tour.steps.situationBtn.title",     textKey: "tour.steps.situationBtn.text" },
  { target: "tour-gauge-air",         page: "/gauges",   tab: null,         titleKey: "tour.steps.gaugeLogique.title",         textKey: "tour.steps.gaugeLogique.text" },
  { target: "tour-gauge-eau",         page: "/gauges",   tab: null,         titleKey: "tour.steps.gaugeSocial.title",         textKey: "tour.steps.gaugeSocial.text" },
  { target: "tour-gauge-terre",       page: "/gauges",   tab: null,         titleKey: "tour.steps.gaugeSensoriel.title",       textKey: "tour.steps.gaugeSensoriel.text" },
  { target: "tour-gauge-feu",         page: "/gauges",   tab: null,         titleKey: "tour.steps.gaugePassion.title",         textKey: "tour.steps.gaugePassion.text" },
  { target: "tour-gauge-volonte",     page: "/gauges",   tab: null,         titleKey: "tour.steps.gaugeMotivation.title",     textKey: "tour.steps.gaugeMotivation.text" },
  { target: "tour-gauge-vital",       page: "/gauges",   tab: null,         titleKey: "tour.steps.gaugeReserve.title",       textKey: "tour.steps.gaugeReserve.text" },
  { target: "tour-gauge-stamina",     page: "/gauges",   tab: null,         titleKey: "tour.steps.gaugeStamina.title",     textKey: "tour.steps.gaugeStamina.text" },
  { target: "tour-settings-profile",  page: "/settings", tab: "identity",   titleKey: "tour.steps.settingsProfile.title",  textKey: "tour.steps.settingsProfile.text" },
  { target: "tour-settings-morning",  page: "/settings", tab: "morning",    titleKey: "tour.steps.settingsMorning.title",  textKey: "tour.steps.settingsMorning.text" },
  { target: "tour-settings-situations", page: "/settings", tab: "situations", titleKey: "tour.steps.settingsSituations.title", textKey: "tour.steps.settingsSituations.text" },
  { target: "tour-settings-proches",  page: "/settings", tab: "proches",    titleKey: "tour.steps.settingsProches.title",  textKey: "tour.steps.settingsProches.text" },
];

export { STEPS };

export default function GuidedTour() {
  const { t } = useTranslation();
  const { active, step, next, prev, end } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const [, forceUpdate] = useState(0);

  const currentStep = STEPS[step];

  // Navigate to correct page (and tab) when step changes
  useEffect(() => {
    if (!active || !currentStep) return;
    if (location.pathname !== currentStep.page) {
      navigate(currentStep.page, {
        state: currentStep.tab ? { tourTab: currentStep.tab } : undefined,
      });
    } else if (currentStep.tab) {
      // Already on the right page but need to switch tab
      window.dispatchEvent(new CustomEvent("tour-set-tab", { detail: currentStep.tab }));
    }
  }, [active, step]); // eslint-disable-line

  // After navigation, switch tab if needed
  useEffect(() => {
    if (!active || !currentStep || !currentStep.tab) return;
    if (location.pathname === currentStep.page) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("tour-set-tab", { detail: currentStep.tab }));
      }, 100);
    }
  }, [location.pathname, active, step]); // eslint-disable-line

  // Find and track target element position
  const updateRect = useCallback(() => {
    if (!active || !currentStep) return;
    const el = document.querySelector(`[data-tour="${currentStep.target}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect(r);
    } else {
      setRect(null);
    }
  }, [active, currentStep]);

  useEffect(() => {
    if (!active) return;
    // Try multiple times to find the element after navigation
    const timers = [100, 300, 600, 1000].map((delay) =>
      setTimeout(() => {
        updateRect();
        forceUpdate((n) => n + 1);
      }, delay)
    );
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect);
    };
  }, [active, step, updateRect, location.pathname]);

  if (!active || !currentStep) return null;

  const PAD = 10;
  const MIN_SPOT = 30; // minimum spotlight size so thin bars are still visible
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let spotTop = 0, spotLeft = 0, spotRight = vw, spotBottom = vh;
  if (rect && rect.width > 0) {
    const cx = (rect.left + rect.right) / 2;
    const cy = (rect.top + rect.bottom) / 2;
    const hw = Math.max(MIN_SPOT, rect.width / 2);
    const hh = Math.max(MIN_SPOT, rect.height / 2);
    spotTop = Math.max(0, cy - hh - PAD);
    spotLeft = Math.max(0, cx - hw - PAD);
    spotRight = Math.min(vw, cx + hw + PAD);
    spotBottom = Math.min(vh, cy + hh + PAD);
  }

  // Tooltip positioning
  const tooltipWidth = 340;
  const showBelow = rect ? spotBottom < vh * 0.6 : false;
  const tooltipTopVal = rect
    ? showBelow
      ? spotBottom + 12
      : spotTop - 12 - 200 // approx height
    : vh / 2 - 120;
  const tooltipLeftVal = rect
    ? Math.max(8, Math.min(vw - tooltipWidth - 8, spotLeft))
    : (vw - tooltipWidth) / 2;

  const overlay = {
    position: "fixed",
    backgroundColor: "rgba(0,0,0,0.72)",
    zIndex: 9000,
    pointerEvents: "none",
  };

  return (
    <>
      {/* Blocking click layer */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 8999 }}
        onClick={(e) => e.stopPropagation()}
      />
      {/* 4 overlay quadrants creating spotlight */}
      {/* Top */}
      <div style={{ ...overlay, top: 0, left: 0, right: 0, height: spotTop }} />
      {/* Bottom */}
      <div style={{ ...overlay, top: spotBottom, left: 0, right: 0, bottom: 0 }} />
      {/* Left */}
      <div
        style={{
          ...overlay,
          top: spotTop,
          left: 0,
          width: spotLeft,
          height: spotBottom - spotTop,
        }}
      />
      {/* Right */}
      <div
        style={{
          ...overlay,
          top: spotTop,
          left: spotRight,
          right: 0,
          height: spotBottom - spotTop,
        }}
      />

      {/* Tooltip card */}
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          top: Math.max(8, tooltipTopVal),
          left: tooltipLeftVal,
          width: tooltipWidth,
          zIndex: 9001,
          p: 2.5,
          backgroundColor: "#1e293b",
          color: "#e2e8f0",
          border: "1px solid #334155",
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 0.5 }}>
          {t("tour.step", { current: step + 1, total: STEPS.length })}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: "#93c5fd" }}>
          {t(currentStep.titleKey)}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.65, color: "#cbd5e1", whiteSpace: "pre-line" }}>
          {t(currentStep.textKey)}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button size="small" onClick={end} sx={{ color: "#64748b", fontSize: "0.75rem" }}>
            {t("tour.skip")}
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            {step > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={prev}
                sx={{ color: "#94a3b8", borderColor: "#334155", fontSize: "0.75rem" }}
              >
                {t("tour.prev")}
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={() => next(STEPS.length)}
              sx={{ backgroundColor: "#2563eb", fontSize: "0.75rem" }}
            >
              {step === STEPS.length - 1 ? t("tour.finish") : t("tour.next")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
}
